/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @author Satyaki Bose
 * @file assignmentSettingsHelpers.js
 * Assignment settings API/form mapping helpers.
 */

import {
  ASSIGNMENT_AVAILABILITY,
  EMPTY_ASSIGNMENT_SETTINGS_VALUES,
  GRADEBOOK_DISPLAY_MODE,
  LATE_WORK_DEADLINE_TYPE,
  buildAssignmentSettingsFormDefaultValues,
  dateTimeUtils
} from '@aet/components';
import { ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS } from '../../../../common/constants';
import CommonUtils from '../../../../common/utils/CommonUtils';
import {
  ASSIGNMENT_SETTINGS_ANALYTICS,
  AVAILABILITY_DAYS_BY_UI_VALUE,
  SCORE_TYPE_ALIASES,
  SCORE_TYPE_API,
  UI_VALUE_BY_AVAILABILITY_DAYS
} from './assignmentSettingsConstants';

const {
  SCORE_TYPE, SCORE_BASE_VALUE, AVAILABLE_BEFORE_DUE_DAYS, DEFAULT_DUE_TIME, COURSE_TIME_ZONE
} = ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS;

const { CUSTOM_FIELD, LATE_WORK_DEADLINE_LABEL, SCORING_METHOD } = ASSIGNMENT_SETTINGS_ANALYTICS;

const FORM_FIELD_TO_ANALYTICS_KEY = {
  gradebookDisplayMode: CUSTOM_FIELD.SCORING_METHOD,
  equalWeightingBaseValue: CUSTOM_FIELD.EQUAL_WEIGHTING_BASE_VALUE,
  defaultDueTime: CUSTOM_FIELD.DEFAULT_DUE_TIME,
  courseTimeZone: CUSTOM_FIELD.COURSE_TIME_ZONE,
  allowLateWork: CUSTOM_FIELD.ALLOW_LATE_WORK,
  lateWorkPercent: CUSTOM_FIELD.LATE_WORK_PENALTY_PCT,
  lateWorkDeadlineType: CUSTOM_FIELD.LATE_WORK_PENALTY_WINDOW,
  lateWorkCustomDate: CUSTOM_FIELD.LATE_WORK_CUSTOM_DATE,
  assignmentAvailability: CUSTOM_FIELD.ASSIGNMENT_AVAILABILITY
};

const LATE_WORK_DEADLINE_ANALYTICS_LABELS = {
  [LATE_WORK_DEADLINE_TYPE.ONE_WEEK]: LATE_WORK_DEADLINE_LABEL.ONE_WEEK,
  [LATE_WORK_DEADLINE_TYPE.TWO_WEEKS]: LATE_WORK_DEADLINE_LABEL.TWO_WEEKS,
  [LATE_WORK_DEADLINE_TYPE.COURSE_END]: LATE_WORK_DEADLINE_LABEL.COURSE_END,
  [LATE_WORK_DEADLINE_TYPE.CUSTOM]: LATE_WORK_DEADLINE_LABEL.CUSTOM
};

// Matches the fields currently exposed on the Assignment settings tab UI.
const VISIBLE_FORM_FIELD_TO_ANALYTICS_KEY = {
  defaultDueTime: CUSTOM_FIELD.DEFAULT_DUE_TIME,
  courseTimeZone: CUSTOM_FIELD.COURSE_TIME_ZONE,
  assignmentAvailability: CUSTOM_FIELD.ASSIGNMENT_AVAILABILITY
};

function normalizeComparableValue(value) {
  if (value == null) {
    return null;
  }

  return String(value);
}

function findOptionLabel(options = [], value) {
  return options.find((option) => option?.value === value)?.label || null;
}

function normalizeScoringMethodForAnalytics(displayMode) {
  return displayMode === GRADEBOOK_DISPLAY_MODE.EQUAL_WEIGHTING
    ? SCORING_METHOD.EQUAL_WEIGHTING
    : SCORING_METHOD.RAW_SCORE;
}

function getModifiedAssignmentSettings(
  initialValues = {},
  formValues = {},
  fieldToAnalyticsKey = FORM_FIELD_TO_ANALYTICS_KEY
) {
  const modifiedSettings = Object.entries(fieldToAnalyticsKey).reduce((settings, [fieldName, analyticsKey]) => {
    if (normalizeComparableValue(initialValues[fieldName]) !== normalizeComparableValue(formValues[fieldName])) {
      settings.push(analyticsKey);
    }

    return settings;
  }, []);

  return modifiedSettings.join(',');
}

function resolveLateWorkPenaltyWindow(formValues = {}, lateWorkDeadlineOptions = []) {
  const { lateWorkDeadlineType, lateWorkCustomDate } = formValues;
  const optionLabel = findOptionLabel(lateWorkDeadlineOptions, lateWorkDeadlineType);

  if (optionLabel) {
    return optionLabel;
  }

  if (lateWorkDeadlineType === LATE_WORK_DEADLINE_TYPE.CUSTOM && lateWorkCustomDate) {
    return lateWorkCustomDate;
  }

  return LATE_WORK_DEADLINE_ANALYTICS_LABELS[lateWorkDeadlineType] || null;
}

/**
 * Builds event 1051 custom fields for global assignment settings.
 * Defaults to visible tab fields; pass trackAllSettings: true when gradebook / late work UI is enabled.
 *
 * @param {Object} formValues
 * @param {Object} initialValues
 * @param {Object} options
 * @param {Array} [options.assignmentAvailabilityOptions]
 * @param {Array} [options.lateWorkDeadlineOptions]
 * @param {Array} [options.timeZoneOptions]
 * @param {boolean} [options.trackAllSettings]
 * @returns {Object}
 */
export function buildUpdateGlobalAssignmentSettingsCustomFields(
  formValues = {},
  initialValues = {},
  {
    assignmentAvailabilityOptions = [],
    lateWorkDeadlineOptions = [],
    timeZoneOptions = [],
    trackAllSettings = false
  } = {}
) {
  const isEqualWeighting = formValues.gradebookDisplayMode === GRADEBOOK_DISPLAY_MODE.EQUAL_WEIGHTING;
  const parsedLateWorkPercent = Number(formValues.lateWorkPercent);
  const fieldToAnalyticsKey = trackAllSettings
    ? FORM_FIELD_TO_ANALYTICS_KEY
    : VISIBLE_FORM_FIELD_TO_ANALYTICS_KEY;
  const modifiedSettings = getModifiedAssignmentSettings(
    initialValues,
    formValues,
    fieldToAnalyticsKey
  );

  const customFields = {
    [CUSTOM_FIELD.MODIFIED_SETTINGS]: modifiedSettings,
    [CUSTOM_FIELD.DEFAULT_DUE_TIME]: formValues.defaultDueTime || null,
    [CUSTOM_FIELD.COURSE_TIME_ZONE]: findOptionLabel(
      timeZoneOptions,
      formValues.courseTimeZone
    ) || formValues.courseTimeZone || null,
    [CUSTOM_FIELD.ASSIGNMENT_AVAILABILITY]: findOptionLabel(
      assignmentAvailabilityOptions,
      formValues.assignmentAvailability
    )
  };

  if (trackAllSettings) {
    customFields[CUSTOM_FIELD.SCORING_METHOD] = normalizeScoringMethodForAnalytics(formValues.gradebookDisplayMode);
    customFields[CUSTOM_FIELD.EQUAL_WEIGHTING_BASE_VALUE] = isEqualWeighting
      ? Number(formValues.equalWeightingBaseValue) || null
      : null;
    customFields[CUSTOM_FIELD.LATE_WORK_PENALTY_PCT] = formValues.allowLateWork && Number.isFinite(parsedLateWorkPercent)
      ? parsedLateWorkPercent
      : null;
    customFields[CUSTOM_FIELD.LATE_WORK_PENALTY_WINDOW] = formValues.allowLateWork
      ? resolveLateWorkPenaltyWindow(formValues, lateWorkDeadlineOptions)
      : null;
  }

  return customFields;
}

export function hasConfigurationEntries(configurations) {
  return Array.isArray(configurations) && configurations.length > 0;
}

function readConfigurationEntry(configurations, key) {
  if (!hasConfigurationEntries(configurations)) {
    return undefined;
  }

  return configurations.find(
    (entry) => CommonUtils.localStringCompare(entry?.key, key)
  )?.value;
}

export function hasAssignmentSettingsConfiguration(configurations) {
  if (!hasConfigurationEntries(configurations)) {
    return false;
  }

  return [
    SCORE_TYPE, SCORE_BASE_VALUE, AVAILABLE_BEFORE_DUE_DAYS, DEFAULT_DUE_TIME, COURSE_TIME_ZONE
  ].some(
    (key) => readConfigurationEntry(configurations, key) != null
  );
}

function normalizeScoreTypeFromApi(value) {
  if (CommonUtils.localStringCompare(value, SCORE_TYPE_API.WEIGHTED_POINTS)) {
    return GRADEBOOK_DISPLAY_MODE.EQUAL_WEIGHTING;
  }

  return GRADEBOOK_DISPLAY_MODE.RAW_SCORE;
}

function normalizeScoreTypeToApi(displayMode) {
  return SCORE_TYPE_ALIASES[displayMode] || SCORE_TYPE_API.RAW_POINTS;
}

function normalizeAvailabilityDaysFromApi(days) {
  const parsedDays = Number(days);

  if (Object.prototype.hasOwnProperty.call(UI_VALUE_BY_AVAILABILITY_DAYS, parsedDays)) {
    return UI_VALUE_BY_AVAILABILITY_DAYS[parsedDays];
  }

  return ASSIGNMENT_AVAILABILITY.IMMEDIATE;
}

function normalizeAvailabilityDaysToApi(assignmentAvailability) {
  const days = AVAILABILITY_DAYS_BY_UI_VALUE[assignmentAvailability];

  if (days === undefined) {
    return AVAILABILITY_DAYS_BY_UI_VALUE[ASSIGNMENT_AVAILABILITY.IMMEDIATE];
  }

  return days;
}

// helper to compute date available from due date and available before due days
export function computeDateAvailable(dueDateIso, availableBeforeDueDays) {
  const days = Number(availableBeforeDueDays) || 0;
  // unhappy path, fallback to current date as usual
  if (!dueDateIso || days <= 0) {
    return new Date().toISOString();
  }

  const dueDate = new Date(dueDateIso);

  // if time invalid, also fallback to currentdate
  if (Number.isNaN(dueDate.getTime())) {
    return new Date().toISOString();
  }

  // minus the "before days"
  dueDate.setUTCDate(dueDate.getUTCDate() - days);

  return dueDate.toISOString();
}

export function buildAssignmentSettingsTextInfo(language) {
  const getMessage = (key) => language.getMessage(key);

  return {
    lmsAlertMessage: getMessage('aet.instructorCourse.assignmentSettings.lmsAlertMessage'),
    gradebookSectionTitle: getMessage('aet.instructorCourse.assignmentSettings.gradebookSectionTitle'),
    gradebookInfoButtonAriaLabel: getMessage('aet.instructorCourse.assignmentSettings.gradebookInfoButtonAriaLabel'),
    rawScoreTitle: getMessage('aet.instructorCourse.assignmentSettings.rawScoreTitle'),
    rawScoreDescription: getMessage('aet.instructorCourse.assignmentSettings.rawScoreDescription'),
    equalWeightingTitle: getMessage('aet.instructorCourse.assignmentSettings.equalWeightingTitle'),
    equalWeightingDescription: getMessage('aet.instructorCourse.assignmentSettings.equalWeightingDescription'),
    baseValueLabel: getMessage('aet.instructorCourse.assignmentSettings.baseValueLabel'),
    availabilitySectionTitle: getMessage('aet.instructorCourse.assignmentSettings.availabilitySectionTitle'),
    availabilityFieldLabel: getMessage('aet.instructorCourse.assignmentSettings.availabilityFieldLabel'),
    dueDatesSectionTitle: getMessage('aet.instructorCourse.assignmentSettings.dueDatesSectionTitle'),
    defaultDueTimeLabel: getMessage('aet.instructorCourse.assignmentSettings.defaultDueTimeLabel'),
    courseTimeZoneLabel: getMessage('aet.instructorCourse.assignmentSettings.courseTimeZoneLabel'),
    cancelButtonLabel: getMessage('aet.instructorCourse.assignmentSettings.cancelButtonLabel'),
    saveButtonLabel: getMessage('aet.instructorCourse.assignmentSettings.saveButtonLabel'),
    gradebookInfoModalTitle: getMessage('aet.instructorCourse.assignmentSettings.gradebookInfoModalTitle'),
    gradebookInfoRawScoreHeading: getMessage('aet.instructorCourse.assignmentSettings.gradebookInfoRawScoreHeading'),
    gradebookInfoRawScoreBody: getMessage('aet.instructorCourse.assignmentSettings.gradebookInfoRawScoreBody'),
    gradebookInfoEqualWeightingHeading: getMessage('aet.instructorCourse.assignmentSettings.gradebookInfoEqualWeightingHeading'),
    gradebookInfoEqualWeightingBody: getMessage('aet.instructorCourse.assignmentSettings.gradebookInfoEqualWeightingBody'),
    baseValueRequired: getMessage('aet.instructorCourse.assignmentSettings.baseValueRequired'),
    baseValueInvalid: getMessage('aet.instructorCourse.assignmentSettings.baseValueInvalid')
  };
}

export function buildAssignmentAvailabilityOptions(language) {
  const getMessage = (key) => language.getMessage(key);

  return [
    {
      value: ASSIGNMENT_AVAILABILITY.IMMEDIATE,
      label: getMessage('aet.instructorCourse.assignmentSettings.availability.immediate')
    },
    {
      value: ASSIGNMENT_AVAILABILITY.FOUR_WEEKS_BEFORE,
      label: getMessage('aet.instructorCourse.assignmentSettings.availability.fourWeeksBefore')
    },
    {
      value: ASSIGNMENT_AVAILABILITY.TWO_WEEKS_BEFORE,
      label: getMessage('aet.instructorCourse.assignmentSettings.availability.twoWeeksBefore')
    },
    {
      value: ASSIGNMENT_AVAILABILITY.ONE_WEEK_BEFORE,
      label: getMessage('aet.instructorCourse.assignmentSettings.availability.oneWeekBefore')
    }
  ];
}

export function mapConfigurationToFormValues(configurations = []) {
  // TODO: map additional API keys when due dates / late work sections are enabled
  const timeZoneOptions = dateTimeUtils.buildTimeZoneOptions();
  const browserTimeZone = dateTimeUtils.getBrowserTimezone?.()
    && dateTimeUtils.matchTimezoneToOptions(timeZoneOptions);
  const defaults = buildAssignmentSettingsFormDefaultValues({
    ...EMPTY_ASSIGNMENT_SETTINGS_VALUES,
    courseTimeZone: browserTimeZone || EMPTY_ASSIGNMENT_SETTINGS_VALUES.courseTimeZone
  });

  if (!hasConfigurationEntries(configurations)) {
    return defaults;
  }

  const scoreType = readConfigurationEntry(configurations, SCORE_TYPE);
  const baseValue = readConfigurationEntry(configurations, SCORE_BASE_VALUE);
  const availableBeforeDueDays = readConfigurationEntry(configurations, AVAILABLE_BEFORE_DUE_DAYS);
  const defaultDueTime = readConfigurationEntry(configurations, DEFAULT_DUE_TIME);
  const courseTimeZone = readConfigurationEntry(configurations, COURSE_TIME_ZONE);

  return buildAssignmentSettingsFormDefaultValues({
    ...defaults,
    ...(scoreType != null && {
      gradebookDisplayMode: normalizeScoreTypeFromApi(scoreType)
    }),
    ...(baseValue != null && {
      equalWeightingBaseValue: String(baseValue)
    }),
    ...(availableBeforeDueDays != null && {
      assignmentAvailability: normalizeAvailabilityDaysFromApi(availableBeforeDueDays)
    }),
    ...(defaultDueTime != null && {
      defaultDueTime: String(defaultDueTime)
    }),
    ...(courseTimeZone != null && {
      courseTimeZone: String(courseTimeZone)
    })
  });
}

export function mapFormValuesToConfigurationPayload(formValues = {}, initialValues = {}) {
  // TODO: include due dates / late work fields in PATCH payload when tab config expands
  const candidateEntries = [
    {
      key: SCORE_TYPE,
      value: normalizeScoreTypeToApi(formValues.gradebookDisplayMode),
      changed: formValues.gradebookDisplayMode !== initialValues.gradebookDisplayMode
    },
    {
      key: SCORE_BASE_VALUE,
      value: Number(formValues.equalWeightingBaseValue)
        || Number(EMPTY_ASSIGNMENT_SETTINGS_VALUES.equalWeightingBaseValue),
      changed: formValues.equalWeightingBaseValue !== initialValues.equalWeightingBaseValue
    },
    {
      key: AVAILABLE_BEFORE_DUE_DAYS,
      value: normalizeAvailabilityDaysToApi(formValues.assignmentAvailability),
      changed: formValues.assignmentAvailability !== initialValues.assignmentAvailability
    },
    {
      key: DEFAULT_DUE_TIME,
      value: formValues.defaultDueTime,
      changed: formValues.defaultDueTime !== initialValues.defaultDueTime
    },
    {
      key: COURSE_TIME_ZONE,
      value: formValues.courseTimeZone,
      changed: formValues.courseTimeZone !== initialValues.courseTimeZone
    }
  ];

  // only send the fields that actually changed from the last saved config
  return {
    configurations: candidateEntries
      .filter((entry) => entry.changed)
      .map(({ key, value }) => ({ key, value }))
  };
}
